/**
 * The poling loop for the water mount `bamboo-log` (H20): a 2 s cycle as 12 keys on a closed Catmull-Rom spline.
 * Each key is where the pole tip is, in the log's frame (a = ahead, b = to the side, in px) and its height over the
 * water (h), plus the body lean (-1..1, forward on the push) and the sleeve sway.
 */
export interface PoleKey {
  readonly a: number;
  readonly b: number;
  readonly h: number;
  readonly lean: number;
}

export const POLE_PERIOD = 2;

/** Lifted high ahead -> dips ahead-and-aside -> pushes back past the feet -> lifts and swings forward. */
export const POLE_KEYS: readonly PoleKey[] = [
  { a: 20, b: 9, h: 14, lean: 0 },
  { a: 26, b: 10, h: 6, lean: 0.1 },
  { a: 28, b: 10, h: 0, lean: 0.3 },
  { a: 22, b: 10, h: 0, lean: 0.6 },
  { a: 12, b: 10, h: 0, lean: 0.9 },
  { a: 2, b: 10, h: 0, lean: 1 },
  { a: -8, b: 9, h: 0, lean: 0.8 },
  { a: -14, b: 9, h: 3, lean: 0.5 },
  { a: -12, b: 8, h: 10, lean: 0.2 },
  { a: -4, b: 8, h: 18, lean: -0.1 },
  { a: 6, b: 8, h: 21, lean: -0.1 },
  { a: 14, b: 8, h: 18, lean: -0.05 },
];

const cr = (p0: number, p1: number, p2: number, p3: number, t: number): number =>
  0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t + (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t);

/** The pole pose at loop phase `ph` (0..1, wraps). */
export function polePose(ph: number): PoleKey {
  const n = POLE_KEYS.length;
  const f = (((ph % 1) + 1) % 1) * n;
  const i = Math.floor(f);
  const t = f - i;
  const k = (j: number): PoleKey => POLE_KEYS[(((i + j) % n) + n) % n]!;
  const v = (get: (q: PoleKey) => number): number => cr(get(k(-1)), get(k(0)), get(k(1)), get(k(2)), t);
  return { a: v((q) => q.a), b: v((q) => q.b), h: Math.max(0, v((q) => q.h)), lean: v((q) => q.lean) };
}
